import React, { FC } from 'react';
import './PermissionTableForm.css';
import { FormikErrors } from '@/Shared';
import { PermissionFormValues, PermissionFormViewModel } from '../../Logic';
import PermissionFormRowCard from './PermissionFormRowCard';

interface PermissionFormCardListProps {
    entityFormVM: PermissionFormViewModel;
    values: PermissionFormValues;
    setFieldValue: (
        field: string,
        value: any,
        shouldValidate?: boolean | undefined
    ) => Promise<void | FormikErrors<PermissionFormValues>>
}

/**
 * =====================================================
 * Component: PermissionFormCardList
 * =====================================================
 *
 * Description:
 * This component renders the permission management UI using a card layout.
 * It allows administrators to manage access rights per resource and globally.
 *
 * Permission levels handled:
 * - Per resource:
 *      • "All actions" (grants all action rights for one resource)
 *      • "All records" (grants access to all records of the resource)
 *      • Individual action rights (read, write, delete, etc.)
 *
 * - Global level ("allDomain"):
 *      • Apply "All actions" to every resource
 *      • Apply "All records" to every resource
 *      • Apply a specific action (e.g., read) across all resources
 *
 * Architectural Notes:
 * - The component centralizes permission logic.
 * - "all" is a derived UI state and is NOT persisted in Formik.
 * - Updates are delegated through setFieldValue to keep Formik as the single source of truth.
 *
 * Author: Samano CASTRE
 * Date: July 17, 2025
 */
const PermissionFormCardList: FC<PermissionFormCardListProps> = ({
    entityFormVM,
    values,
    setFieldValue
}) => {


    /**
     * Applies a full action toggle (read, write, delete, etc.)
     * to a specific resource.
     *
     * Used internally by global and resource-level handlers
     * to ensure consistency when toggling multiple rights at once.
     */
    const changeAllAccess = (checked: boolean, resourceKey: string) => {
        entityFormVM.accessOptions.forEach(accessOpt =>
            setFieldValue(`resourceAccessList.${resourceKey}.${accessOpt.value}`, checked)
        );
    }

    /**
     * Handles the "All actions" toggle.
     *
     * Behavior:
     * - If applied to a specific resource → toggles all action rights
     *   for that resource.
     * - If applied to "allDomain" → propagates the toggle
     *   to every resource in the form.
     */
    const handleGlobalAllChange = (checked: boolean, resourceKey: string) => {

        changeAllAccess(checked, resourceKey);
        if (resourceKey === 'allDomain') {
            Object.keys(values.resourceAccessList)
                .forEach(resourceKey => changeAllAccess(checked, resourceKey));
        }
    }

    /**
     * Handles the "All records" toggle.
     *
     * Behavior:
     * - If applied to a specific resource → updates only that resource.
     * - If applied to "allDomain" → propagates the value
     *   to every resource in the form.
     *
     * This controls data scope (record visibility),
     * not action permissions.
     */
    const handleGlobalOwnerChange = (checked: boolean, resourceKey: string) => {
        setFieldValue(`resourceAccessList.${resourceKey}.allRecords`, checked);

        if (resourceKey === 'allDomain') {
            Object.keys(values.resourceAccessList)
                .forEach(resourceKey =>
                    setFieldValue(`resourceAccessList.${resourceKey}.allRecords`, checked)
                );
        }
    }

    /**
     * Handles a column-level action toggle from the global row.
     *
     * When triggered from "allDomain", it applies a single action
     * (e.g., read or write) across all resources.
     *
     * This ensures column consistency across the permission matrix.
     */
    const handleResourceAllChange = (checked: boolean, resourceKey: string, accessKey: string) => {
        setFieldValue(`resourceAccessList.${resourceKey}.${accessKey}`, checked);

        Object.keys(values.resourceAccessList)
            .forEach(resourceKey =>
                setFieldValue(`resourceAccessList.${resourceKey}.${accessKey}`, checked)
            );
    }


    /**
     * Main permission change dispatcher.
     *
     * Routes checkbox interactions to the appropriate handler:
     * - "all" → triggers action-level bulk update
     * - "allRecords" → triggers data scope bulk update
     * - specific action on "allDomain" → triggers column-level propagation
     *
     * Keeps the UI logic centralized and avoids duplicating
     * permission rules inside child components.
     */
    const handleChange = (
        event: React.ChangeEvent<HTMLInputElement>,
        resourceKey: string, accessKey: string
    ) => {
        const checked = event.target.checked;

        switch (accessKey) {
            case 'all': handleGlobalAllChange(checked, resourceKey); break;
            case 'allRecords': handleGlobalOwnerChange(checked, resourceKey); break;
            default:
                if (resourceKey === 'allDomain') {
                    handleResourceAllChange(checked, resourceKey, accessKey);
                }
                break;
        }
    }

    return <div className="PermissionFormCardList DataCardList" data-testid="DataCardList">
        <div className='DataCardList__heade'>
            <h3 className='DataTable__title'>Permission des {entityFormVM.getRoleLabel(values.role)}s</h3>
        </div>
        <div className='DataCardList__body'>
            <div className="DataCardList__wrapper">

                <PermissionFormRowCard
                    resourceKey='allDomain'
                    resourceLabel='Toutes Ressources'
                    entityFormVM={entityFormVM}
                    values={values}
                    handleChange={
                        (
                            event: React.ChangeEvent<HTMLInputElement>,
                            accessKey: string
                        ) => handleChange(
                            event,
                            'allDomain',
                            accessKey
                        )
                    }
                />

                {
                    entityFormVM.resourceOptions.map(resOpt =>
                        <PermissionFormRowCard
                            key={resOpt.key}
                            resourceKey={resOpt.value as string}
                            entityFormVM={entityFormVM}
                            values={values}
                            handleChange={
                                (
                                    event: React.ChangeEvent<HTMLInputElement>,
                                    accessKey: string
                                ) => handleChange(
                                    event,
                                    resOpt.value as string,
                                    accessKey
                                )
                            }
                        />
                    )
                }
            </div>
        </div>
    </div>;

}

export default PermissionFormCardList;